import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { SubscriptionProvider } from "./context/SubscriptionContext";
import { ThemeProvider } from "./context/ThemeContext";
import { AnalyticsProvider, useAnalytics } from "./context/AnalyticsContext";
import { ChatProvider } from "./context/ChatContext";
import { Toaster } from "./components/ui/sonner";
import { OfflineIndicator, InstallBanner, UpdateNotification } from "./components/PWAComponents";
import ErrorBoundary from "./components/ErrorBoundary";
import { useEffect } from "react";
import { initializeNative, isNative } from "./utils/native";

// Pages
import LandingPage from "./pages/LandingPage";
import AuthPage from "./pages/AuthPage";
import StudioPage from "./pages/StudioPage";
import ProjectsPage from "./pages/ProjectsPage";
import MarketplacePage from "./pages/MarketplacePage";
import UploadPresetPage from "./pages/UploadPresetPage";
import PresetDetailPage from "./pages/PresetDetailPage";
import UserProfilePage from "./pages/UserProfilePage";
import CreatorDashboardPage from "./pages/CreatorDashboardPage";
import PaymentSuccessPage from "./pages/PaymentSuccessPage";
import PresetManagementPage from "./pages/PresetManagementPage";
import AdminModerationPage from "./pages/AdminModerationPage";
import MobileDashboardPage from "./pages/MobileDashboardPage";
import SongMarketplacePage from "./pages/SongMarketplacePage";
import SongUploadPage from "./pages/SongUploadPage";
import SongDetailPage from "./pages/SongDetailPage";
import EffectsLabPage from "./pages/EffectsLabPage";
import AdminDashboard from "./pages/AdminDashboard";
import PricingPage from "./pages/PricingPage";
import ProductsPage from "./pages/ProductsPage";
import StorePage from "./pages/StorePage";
import StoreSuccessPage from "./pages/StoreSuccessPage";
import PurchasesPage from "./pages/PurchasesPage";
import ReferralPage from "./pages/ReferralPage";
import LeaderboardPage from "./pages/LeaderboardPage";
import MessagesPage from "./pages/MessagesPage";
import ChatDrawer from "./components/chat/ChatDrawer";

// Protected Route Wrapper - preserves intended destination
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="w-16 h-16 border-4 border-indigo-500/30 border-t-indigo-500 rounded-full animate-spin" />
      </div>
    );
  }

  if (!user) {
    // Save the intended destination (including search params) for redirect after login
    return <Navigate to="/login" state={{ from: location.pathname + location.search }} replace />;
  }

  return children;
};

// Public Route Wrapper (redirect to studio if logged in)
const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="w-16 h-16 border-4 border-indigo-500/30 border-t-indigo-500 rounded-full animate-spin" />
      </div>
    );
  }

  if (user) {
    // Redirect to saved destination or default to studio
    const from = location.state?.from || "/studio";
    return <Navigate to={from} replace />;
  }

  return children;
};

function AppRoutes() {
  return (
    <Routes>
      {/* Public Routes */}
      <Route
        path="/"
        element={
          <PublicRoute>
            <LandingPage />
          </PublicRoute>
        }
      />
      <Route
        path="/login"
        element={
          <PublicRoute>
            <AuthPage />
          </PublicRoute>
        }
      />
      <Route
        path="/register"
        element={
          <PublicRoute>
            <AuthPage />
          </PublicRoute>
        }
      />

      {/* Protected Routes */}
      <Route
        path="/studio"
        element={
          <ProtectedRoute>
            <StudioPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/projects"
        element={
          <ProtectedRoute>
            <ProjectsPage />
          </ProtectedRoute>
        }
      />

      {/* Marketplace Routes */}
      <Route path="/marketplace" element={<MarketplacePage />} />
      <Route path="/marketplace/:id" element={<PresetDetailPage />} />
      <Route
        path="/marketplace/upload"
        element={
          <ProtectedRoute>
            <UploadPresetPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/marketplace/dashboard"
        element={
          <ProtectedRoute>
            <CreatorDashboardPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/marketplace/manage"
        element={
          <ProtectedRoute>
            <PresetManagementPage />
          </ProtectedRoute>
        }
      />
      <Route path="/marketplace/success" element={<PaymentSuccessPage />} />
      <Route path="/marketplace/user/:userId" element={<UserProfilePage />} />
      <Route
        path="/marketplace/admin"
        element={
          <ProtectedRoute>
            <AdminModerationPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/profile"
        element={
          <ProtectedRoute>
            <UserProfilePage />
          </ProtectedRoute>
        }
      />

      {/* Mobile Dashboard */}
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <MobileDashboardPage />
          </ProtectedRoute>
        }
      />

      {/* Effects Lab */}
      <Route
        path="/effects-lab"
        element={
          <ProtectedRoute>
            <EffectsLabPage />
          </ProtectedRoute>
        }
      />

      {/* Song Marketplace Routes */}
      <Route path="/songs" element={<SongMarketplacePage />} />
      <Route path="/songs/:id" element={<SongDetailPage />} />
      <Route
        path="/songs/upload"
        element={
          <ProtectedRoute>
            <SongUploadPage />
          </ProtectedRoute>
        }
      />

      {/* Admin Dashboard (protected) */}
      <Route
        path="/admin-dashboard"
        element={
          <ProtectedRoute>
            <AdminDashboard />
          </ProtectedRoute>
        }
      />

      {/* Pricing Page (public) */}
      <Route path="/pricing" element={<PricingPage />} />

      {/* Products Page (public) */}
      <Route path="/products" element={<ProductsPage />} />

      {/* Digital Store (public) */}
      <Route path="/store" element={<StorePage />} />
      <Route path="/store/success" element={<StoreSuccessPage />} />
      <Route path="/purchases" element={<PurchasesPage />} />
      <Route path="/referral" element={<ReferralPage />} />

      {/* Leaderboard (public) */}
      <Route path="/leaderboard" element={<LeaderboardPage />} />

      {/* Messages (protected) */}
      <Route
        path="/messages"
        element={
          <ProtectedRoute>
            <MessagesPage />
          </ProtectedRoute>
        }
      />

      {/* Legacy route redirects */}
      <Route path="/practice" element={<Navigate to="/studio" replace />} />
      <Route path="/chords" element={<Navigate to="/studio" replace />} />
      <Route path="/lessons" element={<Navigate to="/studio" replace />} />

      {/* Catch all - redirect to home */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  // Initialize native platform (Capacitor)
  useEffect(() => {
    initializeNative();
  }, []);

  // Register service worker for PWA (web only)
  useEffect(() => {
    if (isNative) return; // Skip for native apps
    
    if ('serviceWorker' in navigator) {
      window.addEventListener('load', () => {
        navigator.serviceWorker.register('/service-worker.js')
          .then((registration) => {
          })
          .catch((error) => {
          });
      });
    }
  }, []);

  return (
    <ErrorBoundary>
      <AnalyticsProvider>
        <div className="App">
          <BrowserRouter>
            <ThemeProvider>
              <AuthProvider>
                <SubscriptionProvider>
                  <ChatProvider>
                  {/* PWA Components */}
                  <OfflineIndicator />
                  <InstallBanner />
                  <UpdateNotification />
                  
                  <AppRoutes />
                  <ChatDrawer />
                  <Toaster position="bottom-right" />
                  </ChatProvider>
                </SubscriptionProvider>
              </AuthProvider>
            </ThemeProvider>
          </BrowserRouter>
        </div>
      </AnalyticsProvider>
    </ErrorBoundary>
  );
}

export default App;
